import { type ReactNode } from 'react';
import { Avatar } from '@/components/ui/avatar';
import { formatFollowers } from '@/utils/formatters';
import { cn } from '@/utils/cn';
import type { BarSegment } from './bar-segment';

const BASE =
  'pointer-events-none absolute bottom-full left-1/2 z-40 mb-2 -translate-x-1/2 min-w-max rounded-lg bg-surface-elevated px-2.5 py-1.5 shadow-lg transition-all duration-150 ease-out';

function photoUrl(seg: BarSegment): string | undefined {
  return seg.photo
    ? `/api/files/team_members/${seg.id}/${seg.photo}`
    : undefined;
}

function formatPct(value: number, goal: number): string {
  if (goal <= 0) return '0%';
  const pct = (value / goal) * 100;
  return `${pct.toFixed(1).replace('.', ',')}%`;
}

function AdminBadge(): ReactNode {
  return (
    <span className="rounded-full border border-white/10 bg-white/[0.04] px-1.5 py-px text-[9px] font-medium uppercase tracking-wider text-text-tertiary">
      Admin
    </span>
  );
}

function PopoverContent({
  seg,
  barGoal,
}: {
  seg: BarSegment;
  barGoal: number;
}): ReactNode {
  return (
    <div className="flex items-center gap-1.5">
      <Avatar src={photoUrl(seg)} name={seg.label} size="sm" />
      <div>
        <div className="flex items-center gap-1">
          <p className="text-[11px] font-medium text-text-primary">
            {seg.label}
          </p>
          {seg.role === 'admin' && <AdminBadge />}
        </div>
        <p className="text-[10px] text-text-tertiary">
          {formatFollowers(seg.value)} ({formatPct(seg.value, barGoal)})
        </p>
      </div>
    </div>
  );
}

export function BarSegmentPopover({
  seg,
  barGoal,
  visible,
}: {
  seg: BarSegment;
  barGoal: number;
  visible: boolean;
}): ReactNode {
  return (
    <div
      className={cn(
        BASE,
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-1'
      )}
    >
      <PopoverContent seg={seg} barGoal={barGoal} />
    </div>
  );
}
